import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { DEFAULT_EVENT_IMAGE } from '@/constants/defaultImages';
import { getSignedEventImageUrl } from '@/utils/eventImageStorage';

const TTL = 3600; // 1 hour

/**
 * Resolves an `events.image` DB value (full legacy URL or bare storage path)
 * to a signed URL suitable for a private Supabase Storage bucket.
 *
 * - Falls back to DEFAULT_EVENT_IMAGE when `stored` is empty or signing fails.
 * - Refreshes the signed URL on window focus / tab visibility change so
 *   long-lived sessions don't get stale URLs.
 */
export function useEventImageSrc(stored: string | null | undefined): string {
  const [src, setSrc] = useState<string>(DEFAULT_EVENT_IMAGE);

  useEffect(() => {
    let cancelled = false;
    setSrc(DEFAULT_EVENT_IMAGE);

    async function resolve() {
      const url = await getSignedEventImageUrl(supabase, stored, TTL);
      if (!cancelled) setSrc(url);
    }

    resolve();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') resolve();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [stored]);

  return src;
}
